// OrderDetailPage.js
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/solid";

import { useGetOrderByIdQuery } from "../../../../services/order/orderApiSlice";
import { useUpdateOrderMutation } from "../../../../services/order/orderMutationApiSlice";
import { StatusPill } from "../../orderTable/orderTableColumnsConfig";

import { CategoryPageSkeleton } from "../../../../component/skeleton/CategoryPageSkeleton";
import { OrderDetailForm } from "../../../../component/order/orderDetail/OrderDetailForm";
import classes from "./orderDetailPage.module.css";

const orderconfig = {
  pending: {
    label: "Pending",
    bg: "#FFF4E5",
    color: "#FF9500",
    Icon: ClockIcon,
  },
  "in process": {
    label: "In Process",
    bg: "#E8F0FE",
    color: "#2563EB",
    Icon: ArrowPathIcon,
  },
  "cancellation requested": {
    label: "Cancellation Requested",
    bg: "#FFF0F6",
    color: "#C21313",
    border: "1px solid #C21313",
    Icon: ExclamationTriangleIcon,
  },
  cancelled: {
    label: "Cancelled",
    bg: "#FDECEA",
    color: "#D92D20",
    Icon: XCircleIcon,
  },
  "cancellation rejected": {
    label: "Cancellation Rejected",
    bg: "#F3E8FF",
    color: "#6D28D9",
    Icon: ShieldCheckIcon,
  },
  fulfilled: {
    label: "Fulfilled",
    bg: "#E6F7EF",
    color: "#00A167",
    Icon: CheckCircleIcon,
  },
  failed: {
    label: "Failed",
    bg: "#FDECEA",
    color: "#B42318",
    Icon: XCircleIcon,
  },
};

const paymentConfig = {
  pending: {
    label: "Pending",
    bg: "#FFF4E5",
    color: "#FF9500",
    Icon: ClockIcon,
  },
  paid: {
    label: "Paid",
    bg: "#E6F7EF",
    color: "#00A167",
    Icon: CheckCircleIcon,
  },
  cancelled: {
    label: "Cancelled",
    bg: "#FDECEA",
    color: "#D92D20",
    Icon: XCircleIcon,
  },
  failed: {
    label: "Failed",
    bg: "#FDECEA",
    color: "#D92D20",
    Icon: XCircleIcon,
  },
};

export const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const {
    data: orderDetail,
    isLoading,
    isError,
    isSuccess,
  } = useGetOrderByIdQuery(orderId);

  const [updateOrder, { isLoading: isUpdating }] = useUpdateOrderMutation();

  const handleUpdate = async (payload) => {
    try {
      await updateOrder({ id: orderId, patchData: payload }).unwrap();

      toast.success("Order updated successfully");
      navigate("/orders");
    } catch (err) {
      toast.error(
        err?.data?.message?.displayMessage ||
          err?.error ||
          "Failed to update order"
      );
    }
  };

 const orderStatusKey = orderDetail?.order_status?.toLowerCase() ?? "";
const orderStatusConfig = orderconfig[orderStatusKey];

const paymentStatusKey = orderDetail?.payment_status?.toLowerCase() ?? "";
const paymentStatusConfig = paymentConfig[paymentStatusKey];
  return isSuccess ? (
    <div className={classes.wrapper}>
      <div className={classes.page}>
        <h2
          className={classes.pageTitle}
        >{`Order ID: ${orderDetail.order_id}`}</h2>

        <div className={classes.page__payment}>
          <h2 className={classes.page__payment__title}>Order: </h2>
          {orderStatusConfig && <StatusPill {...orderStatusConfig} />}
        </div>
        <div className={classes.page__payment}>
          <h2 className={classes.page__payment__title}>Payment: </h2>
          {paymentStatusConfig && <StatusPill {...paymentStatusConfig} />}
        </div>
      </div>

      <OrderDetailForm
        initialValues={orderDetail}
        onSubmit={handleUpdate}
        isSubmitting={isUpdating}
      />
    </div>
  ) : (
    <CategoryPageSkeleton />
  );
};
